import { Divider, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import React, { Dispatch, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
    getDocumentMetadata,
    ProjectSidepanel,
} from '../../../redux/project/sliceProjectSidepanel';
import { IAppReduxStore } from '../../../redux/store';
import { headingFontBold, vars } from '../../../Theme';
import { parseDate } from '../../../util/DataUtils';
import SignatureRecord from '../SignatureRecord/SignatureRecord';
import GroupedAvatars from '../GroupedAvatars/GroupedAvatars';
import PlaceholderCard from './Components/PlaceholderCard';
import LoadingData from './Components/LoadingData';

/**
 * Sidepanel that shows general information on a given document.
 */
export default () => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'sidepanel' });

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Get the replicaId, areaName and viewName from the react router. */
    const { replicaId, areaName, viewName } = useParams();

    /** Get relevant values from Redux store. */
    const { data, loading } = useSelector((store: IAppReduxStore) => store.projectSidepanel);
    const universalId = useSelector((store: IAppReduxStore) => store.projectSidepanel.targetDocId);

    /**
     * Effect that fetches the relevant document data for displaying in the sidepanel.
     */
    useEffect(() => {
        !data[ProjectSidepanel.General] &&
            universalId &&
            dispatch(
                getDocumentMetadata({
                    replicaId,
                    viewName: `${areaName}.${viewName}`,
                    universalId,
                    fields: ['Creator', 'CreationDate', 'wfEditor', 'Signatures'],
                    tab: ProjectSidepanel.General,
                }),
            );
    }, [universalId, data]);

    /** Access the document's general infomation from the Redux store. */
    const sidepanelData = data[ProjectSidepanel.General];

    /**
     * Creates the "details" section of the General tab.
     */
    const createDetailsSection = useCallback(
        () => (
            <Stack>
                <Typography
                    variant='h6'
                    sx={{
                        fontSize: 16,
                        py: 1,
                    }}
                >
                    {t('documentDetails')}
                </Typography>
                {loading && <LoadingData />}
                {!loading && !sidepanelData && <PlaceholderCard translationKey='NoInfoPlaceholder' />}
                {sidepanelData && (
                    <Table
                        size='small'
                        sx={{
                            td: { borderBottomColor: vars.midGray },
                            'tr:last-of-type td': { borderBottom: 0 },
                        }}
                    >
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ ...headingFontBold, px: 0 }}>
                                    {t('CreationDate')}
                                </TableCell>

                                <TableCell sx={{ pr: 0 }}>
                                    {parseDate(sidepanelData['CreationDate'])}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ ...headingFontBold, px: 0 }}>
                                    {t('Creator')}
                                </TableCell>
                                <TableCell sx={{ pr: 0 }}>
                                    <GroupedAvatars data={sidepanelData['Creator']} />
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ ...headingFontBold, px: 0 }}>
                                    {t('Editors')}
                                </TableCell>
                                <TableCell sx={{ pr: 0 }}>
                                    <GroupedAvatars data={sidepanelData['wfEditor']} />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                )}
            </Stack>
        ),
        [loading, sidepanelData],
    );

    /**
     * Creates the "signatures" section of the General tab.
     */
    const createSignatureSection = useCallback(
        () =>
            sidepanelData?.['Signatures']?.length > 0 ? (
                <Stack>
                    <Typography
                        variant='h6'
                        sx={{
                            fontSize: 16,
                            py: 1,
                        }}
                    >
                        {t('Signatures')}
                    </Typography>
                    <Stack>
                        {[...sidepanelData['Signatures']]
                            .sort((a, b) => a.date - b.date)
                            .map(x => (
                                <SignatureRecord key={x.date} signature={x} />
                            ))}
                    </Stack>
                </Stack>
            ) : (
                <></>
            ),
        [sidepanelData],
    );

    return (
        <Stack divider={<Divider flexItem />} sx={{ px: 1, pb: 1, overflow: 'auto' }}>
            {createDetailsSection()}
            {createSignatureSection()}
        </Stack>
    );
};
